
import { useState } from "react";
import { cn } from "@/lib/utils";
import { isValidImageUrl } from "@/utils/imageOptimizer";

export interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  loading?: "lazy" | "eager";
  sizes?: string;
  onError?: () => void;
  fallbackSrc?: string;
}

export const OptimizedImage = ({
  src,
  alt,
  className,
  priority = false,
  loading = "lazy",
  sizes = "100vw",
  onError,
  fallbackSrc = "/placeholder-image.png",
}: OptimizedImageProps) => {
  const [error, setError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  
  // Vérifier si la source est une URL valide
  const isValidUrl = isValidImageUrl(src);
  
  // Préparer l'image de fallback (placeholder)
  const defaultFallback = "/placeholder-image.png";
  const actualFallbackSrc = fallbackSrc || defaultFallback;
  
  // Si l'URL source n'est pas valide ou si une erreur s'est produite, afficher le placeholder
  if (!isValidUrl || error || useFallback) {
    console.log(`Utilisation de l'image fallback pour ${src || 'source inconnue'}`);
    
    // Essayer d'utiliser l'image de fallback
    return (
      <img 
        src={actualFallbackSrc}
        alt={alt}
        className={cn(
          "bg-muted object-cover",
          className
        )}
        onError={() => {
          // Si même l'image de fallback échoue, afficher un div avec texte
          console.error(`Même l'image fallback a échoué: ${actualFallbackSrc}`);
          setUseFallback(true);
          if (onError) onError();
        }}
      />
    );
  }

  try {
    // Support des URLs relatives ou absolues
    const imageUrl = src.startsWith('http') || src.startsWith('/') ? src : `/${src}`;
    
    return (
      <img
        src={imageUrl}
        alt={alt}
        className={className}
        loading={priority ? "eager" : loading}
        onError={(e) => {
          console.error(`Erreur de chargement de l'image: ${src}`);
          setError(true);
          if (onError) {
            onError();
          }
        }}
      />
    );
  } catch (err: any) {
    console.error(`Erreur lors du traitement de l'URL d'image: ${src}`, err);
    if (onError) {
      onError();
    }
    return (
      <img 
        src={actualFallbackSrc}
        alt={alt}
        className={cn(
          "bg-muted object-cover",
          className
        )}
        onError={() => setUseFallback(true)}
      />
    );
  }
};
